"use client";

import { BellOff, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PushNudgeCopy } from "../lib/push-nudge-copy";

export type PushNudgeRescueProps = {
  copy: PushNudgeCopy;
  onPrimary: () => void;
  onDismiss: () => void;
  className?: string;
};

/**
 * V5 — the rescue row.
 *
 * Shown when the system dialog is gone: permission was refused, or the budget
 * has been spent elsewhere. There is no CTA here that can raise a prompt,
 * because none exists — the only route back is the platform's own Settings, so
 * that is what the button says and does.
 *
 * Shaped as a settings row rather than a promo card on purpose. This is a
 * statement of fact about the user's device, not an offer, and dressing it as
 * an offer would be the second time the app asked for something it cannot
 * deliver.
 *
 * No medallion and no mock rows: nothing is being sold, and showing examples of
 * notifications the user is not receiving reads as taunting.
 */
export function PushNudgeRescue({
  copy,
  onPrimary,
  onDismiss,
  className,
}: PushNudgeRescueProps) {
  return (
    <section
      aria-label={copy.headline}
      className={cn(
        "rounded-2xl border border-border bg-surface px-4 py-3",
        "dark:border-glass-border-10 dark:bg-glass-bg-05",
        className,
      )}
    >
      {/*
        Stacks below 480px and only becomes a single row when there is genuinely
        width for one. As a always-row it collapsed at 375px: the action group
        cannot shrink, so the text column was squeezed to about ten characters
        and the sentence explaining what the user had lost became unreadable —
        on the exact screen whose whole job is explaining it.
      */}
      <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-glass-bg-06 text-text-tertiary dark:bg-glass-bg-10"
          >
            <BellOff className="h-[18px] w-[18px]" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-[15px] leading-tight font-medium text-text-primary">
              {copy.headline}
            </p>
            <p className="mt-0.5 text-[13px] leading-snug text-text-secondary">
              {copy.subhead}
            </p>
          </div>

          {/* The dismiss stays on the text line when stacked, so the row never
              grows a third band just to hold one icon. */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="-mr-1 h-9 w-9 shrink-0 rounded-full text-text-tertiary hover:bg-glass-bg-04 min-[480px]:hidden dark:hover:bg-glass-bg-06"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ×
            </span>
          </Button>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="outline"
            onClick={onPrimary}
            className="h-10 w-full rounded-full border-border px-4 text-[13px] font-medium text-text-primary min-[480px]:h-9 min-[480px]:w-auto min-[480px]:px-3 dark:border-glass-border-14 dark:text-text-primary"
          >
            {copy.primaryLabel}
            <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="hidden h-9 w-9 rounded-full text-text-tertiary hover:bg-glass-bg-04 min-[480px]:flex dark:hover:bg-glass-bg-06"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ×
            </span>
          </Button>
        </div>
      </div>
    </section>
  );
}
